/**
 * Grant dialog for permission management (full-admin only).
 *
 * Mirrors AddLocationMemberDialog: a person/role toggle, a UserSelect for users
 * or a role picker for roles, and a relation picker scoped to the target. The
 * write is server-authoritative (see usePermissions.ts) — the dialog stays open
 * and surfaces the server's message on failure, and closes only once the server
 * confirms the grant landed. No optimistic close.
 */

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { UserSelect } from '@/components/admin/UserSelect';
import { errMessage } from './OrgUi';
import { useRoles } from './useOrg';
import { useGrantPermission } from './usePermissions';
import type { PermissionGrantBody, PermissionPrincipalType } from '@/api/endpoints/admin';

const selectClass =
  'h-9 w-full rounded-md border border-neutral-200 bg-white px-2 text-sm focus:border-primary focus:outline-none';

export function GrantPermissionDialog({
  open,
  onOpenChange,
  resourceType,
  resourceId,
  targetName,
  relations,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resourceType: string;
  resourceId: string;
  targetName: string;
  /** Relations grantable on this target — from /grants:targets. */
  relations: string[];
}) {
  const grant = useGrantPermission();
  // reset is stable (TanStack mutation); aliasing it lets the open-effect depend
  // on it explicitly (exhaustive-deps) without depending on the whole mutation
  // object, which is a fresh reference each render.
  const resetGrant = grant.reset;
  const { data: rolesData } = useRoles();
  const roles = rolesData ?? [];

  const [principalType, setPrincipalType] = useState<PermissionPrincipalType>('user');
  const [principalId, setPrincipalId] = useState('');
  const [relation, setRelation] = useState('');

  // Reset every open; default the relation to the first available (the
  // vocabulary is config-sourced, so there is no hardcoded fallback).
  useEffect(() => {
    if (!open) return;
    setPrincipalType('user');
    setPrincipalId('');
    setRelation(relations[0] ?? '');
    resetGrant();
  }, [open, relations, resetGrant]);

  const canSubmit = principalId.trim().length > 0 && relation.length > 0;

  const submit = () => {
    if (!canSubmit) return;
    const body: PermissionGrantBody = {
      resource_type: resourceType,
      resource_id: resourceId,
      principal_type: principalType,
      principal_id: principalId.trim(),
      relation,
    };
    grant.mutate(body, { onSuccess: () => onOpenChange(false) });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Grant access · {targetName}</DialogTitle>
          <DialogDescription>
            Give a person or a whole role one of this target&apos;s relations.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex gap-2">
            {(['user', 'role'] as PermissionPrincipalType[]).map((t) => (
              <button
                key={t}
                onClick={() => {
                  setPrincipalType(t);
                  setPrincipalId('');
                }}
                className={`flex-1 rounded-md border px-3 py-1.5 text-sm transition-colors ${
                  principalType === t
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-neutral-200 text-neutral-600 hover:bg-neutral-50'
                }`}
              >
                {t === 'user' ? 'Person' : 'Role'}
              </button>
            ))}
          </div>

          {principalType === 'user' ? (
            <UserSelect onSelect={setPrincipalId} onClear={() => setPrincipalId('')} />
          ) : (
            <select className={selectClass} value={principalId} onChange={(e) => setPrincipalId(e.target.value)}>
              <option value="">Select a role…</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          )}

          <div className="space-y-1">
            <label className="text-xs font-medium text-neutral-500">Relation</label>
            <select className={selectClass} value={relation} onChange={(e) => setRelation(e.target.value)}>
              {relations.length === 0 && <option value="">No relations available</option>}
              {relations.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          {grant.isError && (
            <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMessage(grant.error)}</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit || grant.isPending}>
            {grant.isPending ? <Spinner className="h-4 w-4" /> : 'Grant'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
